import React from 'react';
import { ShieldAlert, Unlock } from 'lucide-react';
import PermissionsContainer from '../../components/PermissionsContainer';
import { useTryPermissionsMode } from '../../contexts/PermissionsModeContext';
import { useAdminOverride, PRO_ADMIN_OVERRIDE_PERMISSION_ID } from './AdminOverrideContext';

/**
 * Admin Override Mode controls (#33501) — the toggle and the banner that says the pro is
 * in it.
 *
 * Both render NOTHING unless the caller holds `pro-admin-override` on a record that can
 * take one, so for everybody else the pro is exactly as it was.
 *
 * Red, not the amber Change All uses: these are different kinds of caution. Change All
 * warns that an edit will fan out to other records; override warns that this record's own
 * protections are off. Someone glancing at the screen should be able to tell which.
 */

/** Toggle for the tab strip. Sits beside the Follow Up bookmark so it is present in both
 *  page and drawer mode — the drawer is a real pro view and needs the same capability.
 *
 *  Wrapped in a PermissionsContainer so it carries the standard permissions-mode cog:
 *  `pro-admin-override` starts granted to Developers alone, and widening it to another
 *  group should be a thing someone can do from the page rather than a migration.
 *
 *  Note the container renders its children in permissions mode REGARDLESS of access
 *  level — that is how an admin configures a component they cannot themselves use — so
 *  the inner button has to render there too, or the cog would have nothing to attach to.
 *  It renders disabled in that case: you are configuring the control, not using it. */
export const AdminOverrideButton: React.FC = () => (
  <PermissionsContainer
    componentId={PRO_ADMIN_OVERRIDE_PERMISSION_ID}
    componentName="Pro Details — Admin Override"
    defaultHidden
  >
    <AdminOverrideToggle />
  </PermissionsContainer>
);

const AdminOverrideToggle: React.FC = () => {
  const { eligible, available, active, setActive } = useAdminOverride();
  const permissionsMode = useTryPermissionsMode()?.permissionsMode ?? false;
  // Nothing to override on a template or a record being created — and nothing to
  // configure either, so no cog on those.
  if (!eligible) return null;
  // Visible and usable at EDIT only. The container hides level 0 by itself, but level 1
  // (View) would otherwise show a button that cannot do anything. In permissions mode the
  // button stays on screen so it can be configured.
  if (!available && !permissionsMode) return null;
  return (
    <button
      type="button"
      onClick={() => setActive(!active)}
      disabled={!available}
      aria-pressed={active}
      title={
        active
          ? 'Admin Override is ON — fields locked by this order’s state are editable. Turn off when done.'
          : 'Admin Override — unlock fields this order’s state has locked (invoiced, paid). This pro only, until you leave it.'
      }
      className={`${
        active
          ? 'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[0.75rem] font-semibold bg-red-600 dark:bg-red-700 text-white border border-red-700 dark:border-red-600 hover:bg-red-700 dark:hover:bg-red-600'
          : 'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[0.75rem] border border-slate-300 dark:border-gray-600 text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800'
      }${available ? '' : ' opacity-50 cursor-not-allowed'}`}
    >
      {active ? <ShieldAlert className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
      {active ? 'Override ON' : 'Admin Override'}
    </button>
  );
};

/**
 * The "this pro is in override" banner. Deliberately loud and always accompanied by a way
 * out: the mode is meant to be entered for one correction and left, and the surest way to
 * make that happen is to keep saying so.
 *
 * It also states the boundary, because the mode does less than its name suggests and a
 * user who expects more will file a bug: fields withheld by the user's OWN permissions
 * stay withheld, and a few locks (deleting an invoiced order, derived totals) never open.
 */
export const AdminOverrideBanner: React.FC = () => {
  const { active, setActive } = useAdminOverride();
  if (!active) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-1.5 bg-red-50 dark:bg-red-900/20 border-b border-red-300 dark:border-red-800 text-[0.75rem] text-red-800 dark:text-red-200">
      <span className="flex items-center gap-1.5">
        <ShieldAlert className="w-3.5 h-3.5 shrink-0" />
        <span>
          <strong>Admin Override is ON</strong> — fields locked by this order’s state are
          editable, and every change is tagged in History. Your own permissions still
          apply, and a few locks never open.
        </span>
      </span>
      <button
        type="button"
        onClick={() => setActive(false)}
        className="shrink-0 px-2 py-0.5 rounded border border-red-400 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/40"
      >
        Turn off
      </button>
    </div>
  );
};
